import React from 'react';
import ReportPage from '../ReportChart/ReportPage';
import { statusOptions } from "helpers/constants";
import { getPlannedWorkCompliance } from "services/analytics";
import useChartColors from '../ReportChart/hooks/useChartColors';

const getStatusName = (statusId) => {
  const status = statusOptions.find(s => s.id === statusId);
  return status ? status.name : 'Unknown';
};

const PlannedWorkComplianceReport = () => {
  const { customCaptions } = JSON.parse(sessionStorage.getItem("me")) || JSON.parse(localStorage.getItem("me"));
  const chartColors = useChartColors();

  const uniqueStatuses = statusOptions.map(s => ({ id: s.id, name: s.name, type: s.groupBy }));

  const chartConfig = {
    id: 'planned-work-compliance',
    title: 'Planned Work Compliance',
    currentDrilldownLabel: `${customCaptions.servicesPlural || 'Services'} Status`,
    fetchData: async (params) => {
      return await getPlannedWorkCompliance(params);
    },
    allowedChartTypes: ['pie', 'vertical-bar', 'stacked-bar', 'horizontal-stacked-bar'],
    defaultChartType: 'pie',
    axisConfig: {
      categoryAxis: { dataKey: 'workStatus' },
      valueAxes: [{
        label: 'Count'
      }]
    },
    series: () => {
      return uniqueStatuses.map((status, index) => {
        const colorType = status.type === 'Complete' ? 'positive' : 'negative';
        const color = chartColors.getColorByIndex(index, colorType);

        return {
          label: status.name,
          key: status.name,
          showLegend: true,
          colors: [color],
          defaultColor: color,
          getColorByIndex: () => color,
          drilldown: true,
          seriesMetadata: { statusId: status.id }
        };
      });
    },
    transformData: (response) => {
      const rawData = response?.data?.data || [];
      if (rawData.length === 0) return [];

      const workStatusMap = new Map();

      // Initialize map with known work statuses and all possible status columns
      const workStatuses = ["Outstanding", "Completed"];
      workStatuses.forEach(ws => {
        const initialEntry = { workStatus: ws };
        uniqueStatuses.forEach(status => {
          initialEntry[status.name] = 0;
        });
        workStatusMap.set(ws, initialEntry);
      });

      // Populate the map with counts from rawData
      rawData.forEach(item => {
        const workStatus = item.workStatus;
        const statusName = getStatusName(item.status);
        if (workStatusMap.has(workStatus) && statusName !== 'Unknown') {
          const entry = workStatusMap.get(workStatus);
          entry[statusName] = (entry[statusName] || 0) + item.count;
        }
      });

      return Array.from(workStatusMap.values());
    },
    filters: {
      timeframe: {
        allowCustomRange: true,
        defaultRange: 'show-all',
      },
      department: { multiSelect: true },
      model: true
    },
    viewData: '/app/services',
    drilldown: {
      // First level drilldown (by Status)
      allowedChartTypes: ['pie', 'vertical-bar'],
      defaultChartType: 'pie',
      drilldownLabel: `${customCaptions.rolesPlural || 'Roles'}`,
      axisConfig: {
        categoryAxis: { dataKey: 'label' },
        valueAxes: [{
          label: `${customCaptions?.role || 'Role'} Count`
        }]
      },
      series: [
        {
          label: `${customCaptions?.role || 'Role'}`,
          key: 'count',
          showLegend: false
        }
      ],
      transformData: (response) => {
        return (response?.data?.data || []).map(item => ({
          ...item,
          roleId: item.id
        }));
      },
      currentDrilldownLabel: `${customCaptions?.role || 'Role'}`,
      getParams: (dataPoint) => {
        return {
          status: dataPoint?.seriesMetadata?.statusId
        };
      }
    }
  };

  return (
    <ReportPage
      title="Planned Work Compliance"
      chartConfig={chartConfig}
    />
  );
};

export default PlannedWorkComplianceReport; 
